import { Box, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { useContext, useEffect, useMemo, useState } from "react";
import { operationsApi } from "../../../../services/api";
import type { CriticalFailure } from "../../../../services/api/routes/operations";
import PrimaryBaseButton from "../../../../main/components/PrimaryBaseButton";

interface FalhaCategoria {
    nome: string;
    rootDescription?: string;
    quantidade: number;
    cor: string;
}

interface DistribuicaoFalhasChartProps {
    clusterId?: string;
    usinaId?: number;
}

const COLORS = [
    "#F85F56",
    "#FB773B",
    "#2CCBB8",
    "#7C83FD",
    "#A78BFA",
    "#22C55E",
    "#F59E0B",
    "#06B6D4",
    "#EC4899",
    "#84CC16",
];

export default function DistribuicaoFalhasChart({ clusterId, usinaId }: DistribuicaoFalhasChartProps) {
    const LIST_PAGE_SIZE = 12;
    const { theme } = useContext(MainContext);
    const [criticalFailures, setCriticalFailures] = useState<CriticalFailure[]>([]);
    const [loading, setLoading] = useState(false);
    const [visibleItemsCount, setVisibleItemsCount] = useState(LIST_PAGE_SIZE);

    useEffect(() => {
        const fetchCriticalFailures = async () => {
            try {
                setLoading(true);
                const response = await operationsApi.getRecentCriticalFailures({
                    ClusterId: clusterId,
                    UsinaId: usinaId,
                });
                setCriticalFailures(response.criticalFailures || []);
            } catch (error) {
                console.error("Erro ao buscar falhas para distribuição:", error);
                setCriticalFailures([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCriticalFailures();
    }, [clusterId, usinaId]);

    useEffect(() => {
        setVisibleItemsCount(LIST_PAGE_SIZE);
    }, [criticalFailures]);

    const getStableColorByCode = (code: string) => {
        const hash = code.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return COLORS[hash % COLORS.length];
    };

    const categorias: FalhaCategoria[] = useMemo(() => {
        const codeDataMap = new Map<string, { quantidade: number; rootDescription?: string }>();
        criticalFailures.forEach((failure) => {
            const code = failure.code || "Sem código";
            const rootDescription = failure.rootDescription || failure.name;
            const currentData = codeDataMap.get(code);

            if (currentData) {
                codeDataMap.set(code, {
                    quantidade: currentData.quantidade + 1,
                    rootDescription: currentData.rootDescription || rootDescription,
                });
                return;
            }

            codeDataMap.set(code, {
                quantidade: 1,
                rootDescription,
            });
        });

        return Array.from(codeDataMap.entries())
            .sort((a, b) => b[1].quantidade - a[1].quantidade)
            .map(([nome, data]) => ({
                nome,
                rootDescription: data.rootDescription,
                quantidade: data.quantidade,
                cor: getStableColorByCode(nome)
            }));
    }, [criticalFailures]);

    const visibleCategorias = useMemo(() => {
        return categorias.slice(0, visibleItemsCount);
    }, [categorias, visibleItemsCount]);

    const total = categorias.reduce((sum, cat) => sum + cat.quantidade, 0);

    const chartOption = {
        tooltip: {
            trigger: "item",
            formatter: "{b}: {c} ({d}%)",
            backgroundColor: theme.backgroundColor || "#1e1e1e",
            textStyle: {
                color: theme.primaryTextColor
            }
        },
        legend: {
            show: false
        },
        series: [
            {
                type: "pie",
                radius: ["40%", "70%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 4,
                    borderWidth: 2
                },
                label: {
                    show: true,
                    position: "outside",
                    formatter: "{d}%",
                    fontSize: 13,
                    fontWeight: "bold",
                    color: "#FFF",
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 15,
                        fontWeight: "bold"
                    }
                },
                labelLine: {
                    show: true,
                    length: 15,
                    length2: 10,
                    lineStyle: {
                        color: theme.cardBorderColor
                    }
                },
                data: categorias.filter((data) => data.quantidade > 0).map(cat => ({
                    value: cat.quantidade,
                    name: cat.nome,
                    itemStyle: { color: cat.cor }
                }))
            }
        ],
        graphic: [
            {
                type: "text",
                left: "center",
                top: "43%",
                z: 999,
                style: {
                    text: loading ? "..." : total.toString(),
                    textAlign: "center",
                    fill: theme.primaryTextColor || "#fff",
                    fontSize: 32,
                    fontWeight: "bold"
                }
            },
            {
                type: "text",
                left: "center",
                top: "55%",
                z: 999,
                style: {
                    text: "Falhas",
                    textAlign: "center",
                    fill: theme.secondaryTextColor || "#aaa",
                    fontSize: 14,
                    fontWeight: "normal"
                }
            }
        ]
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, marginBottom: "16px", color: theme.primaryTextColor, textAlign: "left" }}>
                Distribuição de Falhas por Código
            </Typography>

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                {loading && (
                    <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor, textAlign: "center", marginBottom: "8px" }}>
                        Carregando distribuição...
                    </Typography>
                )}
                <ReactECharts
                    option={chartOption}
                    style={{ height: "280px", width: "100%" }}
                    opts={{ renderer: "canvas" }}
                />

                <Box sx={{ marginTop: "16px", maxHeight: "280px", overflowY: "auto", paddingRight: "6px" }}>
                    {!loading && categorias.length === 0 && (
                        <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor, textAlign: "center", padding: "12px" }}>
                            Nenhuma falha encontrada
                        </Typography>
                    )}
                    {visibleCategorias.map((cat, index) => {
                        const percentage = total > 0 ? ((cat.quantidade / total) * 100).toFixed(0) : "0";
                        return (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "8px",
                                    backgroundColor: "#202020b0",
                                    padding: "6px 8px",
                                    borderRadius: "6px",
                                    border: `1px solid #334155`
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <Box
                                        sx={{
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "2px",
                                            backgroundColor: cat.cor
                                        }}
                                    />
                                    <Typography sx={{ fontSize: "13px", color: theme.primaryTextColor }}>
                                        {cat.nome}{cat.rootDescription ? ` - ${cat.rootDescription}` : ""}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: theme.primaryTextColor }}>
                                        {cat.quantidade}
                                    </Typography>
                                    <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor, minWidth: "35px", textAlign: "right" }}>
                                        {percentage}%
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                    {!loading && visibleItemsCount < categorias.length && (
                        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "10px", marginBottom: "6px" }}>
                            <PrimaryBaseButton
                                isActive={false}
                                onClick={() => setVisibleItemsCount((prev) => prev + LIST_PAGE_SIZE)}
                                sx={{ height: "28px" }}
                                title="Carregar mais"
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
